import { useEffect, useRef, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';

function defaultMapDoc(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? null,
  };
}

export function usePaginatedTenantList({
  tenantId,
  user,
  memberships,
  userLoading,
  pageSize,
  buildPageQuery,
  fetchPage,
  mapDoc = defaultMapDoc,
  logLabel = 'items',
  onSnapshotSuccess,
  onSnapshotError,
  onFetchError,
}) {
  const [items, setItems] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const pageCursorsRef = useRef(pageCursors);
  pageCursorsRef.current = pageCursors;

  useEffect(() => {
    setPageIndex(0);
    setPageCursors([]);
    setHasNextPage(false);
    setItems([]);
  }, [user?.uid, user?.unit, tenantId]);

  useEffect(() => {
    if (userLoading || !user || !tenantId || pageIndex !== 0) return undefined;

    const listQuery = buildPageQuery(user, tenantId, memberships);
    const unsubscribe = onSnapshot(listQuery, (querySnapshot) => {
      setItems(querySnapshot.docs.map(mapDoc));
      setHasNextPage(querySnapshot.docs.length === pageSize);
      onSnapshotSuccess?.();

      if (querySnapshot.docs.length > 0) {
        setPageCursors((prev) => {
          const next = [...prev];
          next[0] = querySnapshot.docs[querySnapshot.docs.length - 1];
          return next;
        });
      }
    }, (err) => {
      console.error(`Error fetching ${logLabel}:`, err);
      onSnapshotError?.(err);
    });

    return () => unsubscribe();
  }, [
    user,
    userLoading,
    pageIndex,
    tenantId,
    memberships,
    buildPageQuery,
    mapDoc,
    pageSize,
    logLabel,
    onSnapshotSuccess,
    onSnapshotError,
  ]);

  useEffect(() => {
    if (userLoading || !user || !tenantId || pageIndex === 0) return undefined;

    let cancelled = false;

    const loadPage = async () => {
      const cursor = pageCursorsRef.current[pageIndex - 1];
      if (!cursor) {
        setPageIndex(0);
        return;
      }

      setPageLoading(true);
      try {
        const { items: pageItems, lastVisible } = await fetchPage(
          user,
          tenantId,
          memberships,
          { startAfterDoc: cursor },
        );
        if (cancelled) return;

        if (pageItems.length === 0) {
          setPageIndex(0);
          return;
        }

        setItems(pageItems);
        setHasNextPage(pageItems.length === pageSize);

        if (lastVisible) {
          setPageCursors((prev) => {
            const next = [...prev];
            next[pageIndex] = lastVisible;
            return next;
          });
        }
      } catch (error) {
        console.error(`Error fetching ${logLabel} page:`, error);
        onFetchError?.(error);
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [pageIndex, user, userLoading, tenantId, memberships, fetchPage, pageSize, logLabel, onFetchError]);

  const goToPrevPage = () => {
    if (pageIndex === 0 || pageLoading) return false;
    setPageIndex((prev) => prev - 1);
    return true;
  };

  const goToNextPage = () => {
    if (!hasNextPage || pageLoading) return false;
    setPageIndex((prev) => prev + 1);
    return true;
  };

  return {
    items,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  };
}
